import { useEffect, useRef, useState } from "react";
import { ChevronDown, Search, X, Pencil } from "lucide-react";

/**
 * Mobile-friendly searchable select with optional "Otro / Especificar manual" fallback.
 *
 * Props:
 *  - options: string[] — the list of predefined values.
 *  - value: string — currently selected value (can be from `options` or a custom manual value).
 *  - onChange: (newValue: string) => void
 *  - placeholder: string
 *  - allowManual: boolean — whether to show "Otro / Especificar manual" option.
 *  - manualLabel: string — label for the manual option.
 *  - disabled: boolean
 *  - testid: string — used as prefix for data-testid attributes.
 *  - emptyLabel: string — text shown when no options are available.
 */
export default function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "Seleccionar…",
  allowManual = true,
  manualLabel = "Otro / Especificar manual…",
  disabled = false,
  testid = "searchable-select",
  emptyLabel = "Sin resultados",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [manual, setManual] = useState(false);
  const wrapRef = useRef(null);
  const searchInputRef = useRef(null);
  const manualInputRef = useRef(null);

  // Value is "custom" when it exists and is NOT in the options list.
  const isCustomValue =
    !!value && !options.some((o) => o.toLowerCase() === value.toLowerCase());

  // Sync manual mode whenever the value or options change externally.
  useEffect(() => {
    setManual(isCustomValue);
  }, [isCustomValue]);

  // Close dropdown when clicking outside.
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open]);

  const filtered = options.filter((o) =>
    o.toLowerCase().includes(query.toLowerCase()),
  );

  if (manual) {
    return (
      <div className="space-y-1" ref={wrapRef}>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Pencil size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={manualInputRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              className="input-base pl-9"
              disabled={disabled}
              data-testid={`${testid}-manual-input`}
              autoFocus
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setManual(false);
              onChange("");
            }}
            className="btn-secondary shrink-0"
            data-testid={`${testid}-manual-back`}
            disabled={disabled}
          >
            <X size={16} />
          </button>
        </div>
        <div className="text-[10px] text-slate-500 font-mono">
          Escribiendo manualmente · toca la X para volver a la lista
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          setTimeout(() => searchInputRef.current?.focus(), 30);
        }}
        disabled={disabled}
        className={`input-base w-full text-left flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed ${
          !value ? "text-slate-400" : "text-slate-900"
        }`}
        data-testid={`${testid}-trigger`}
      >
        <span className="truncate">{value || placeholder}</span>
        <ChevronDown
          size={16}
          className={`text-slate-400 shrink-0 ml-2 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          className="absolute z-30 top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden flex flex-col"
          style={{ maxHeight: "min(280px, 55vh)" }}
          data-testid={`${testid}-dropdown`}
        >
          <div className="p-2 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
            <Search size={14} className="text-slate-400 shrink-0" />
            <input
              ref={searchInputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar…"
              className="bg-transparent outline-none flex-1 text-sm min-w-0"
              data-testid={`${testid}-search`}
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  searchInputRef.current?.focus();
                }}
                className="text-slate-400 hover:text-slate-600 shrink-0"
                data-testid={`${testid}-search-clear`}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {options.length === 0 && !allowManual && (
              <div className="px-3 py-4 text-slate-500 text-sm text-center">{emptyLabel}</div>
            )}
            {filtered.map((opt) => (
              <button
                type="button"
                key={opt}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                  setQuery("");
                }}
                className={`w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 transition-colors ${
                  value === opt
                    ? "bg-blue-50 text-blue-700 font-semibold"
                    : "text-slate-700"
                }`}
                data-testid={`${testid}-opt`}
              >
                {opt}
              </button>
            ))}
            {options.length > 0 && filtered.length === 0 && (
              <div className="px-3 py-3 text-slate-500 text-sm text-center">{emptyLabel}</div>
            )}
            {allowManual && (
              <button
                type="button"
                onClick={() => {
                  setManual(true);
                  setOpen(false);
                  setQuery("");
                  onChange("");
                }}
                className="w-full text-left px-3 py-2.5 text-sm border-t border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 italic"
                data-testid={`${testid}-opt-manual`}
              >
                {manualLabel}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
